import { useState, useRef, useEffect, useCallback } from 'react'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { useReplyToComment, useSuggestReply } from '@/hooks/use-social-listening'
import { cn } from '@/lib/utils'
import type { CommentModeration } from '@/lib/api/modules/social-listening'

const MAX_CHARS = 300
const WORD_REVEAL_MS = 60

interface ReplyDialogProps {
  item: CommentModeration
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ReplyDialog({ item, open, onOpenChange }: ReplyDialogProps) {
  const [message, setMessage] = useState('')
  const [unhide, setUnhide] = useState(false)
  const [revealedWords, setRevealedWords] = useState<string[]>([])
  const [totalWords, setTotalWords] = useState<string[]>([])
  const [isRevealing, setIsRevealing] = useState(false)
  const [hasSuggested, setHasSuggested] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const revealRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const replyToComment = useReplyToComment()
  const suggestReply = useSuggestReply()

  const clearReveal = useCallback(() => {
    if (revealRef.current) {
      clearInterval(revealRef.current)
      revealRef.current = null
    }
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        clearReveal()
        setMessage('')
        setUnhide(false)
        setRevealedWords([])
        setTotalWords([])
        setIsRevealing(false)
        setHasSuggested(false)
      }
      onOpenChange(next)
    },
    [clearReveal, onOpenChange]
  )

  useEffect(() => {
    return () => {
      clearReveal()
    }
  }, [clearReveal])

  const runWordReveal = useCallback((text: string) => {
    const words = text.split(/(\s+)/) // preserve whitespace tokens
    setTotalWords(words)
    setRevealedWords([])
    setIsRevealing(true)
    setMessage('') // clear textarea — reveal overlay takes over

    let idx = 0
    revealRef.current = setInterval(() => {
      idx += 1
      setRevealedWords(words.slice(0, idx))

      if (idx >= words.length) {
        clearInterval(revealRef.current!)
        revealRef.current = null
        setIsRevealing(false)
        setHasSuggested(true)
        // Transfer revealed text to textarea for editing
        setMessage(text)
        setRevealedWords([])
        setTotalWords([])

        requestAnimationFrame(() => {
          const el = textareaRef.current
          if (el) {
            el.focus()
            el.setSelectionRange(el.value.length, el.value.length)
          }
        })
      }
    }, WORD_REVEAL_MS)
  }, [])

  const handleSuggest = () => {
    clearReveal()
    setIsRevealing(false)

    suggestReply.mutate(item.id, {
      onSuccess: (data) => {
        if (data.suggestion) {
          runWordReveal(data.suggestion)
        }
      },
      onError: (error) => {
        toast.error('Erro ao sugerir resposta', {
          description: (error as Error).message || 'Tente novamente.',
        })
      },
    })
  }

  const handleSend = () => {
    replyToComment.mutate(
      { id: item.id, data: { message, unhide } },
      {
        onSuccess: () => {
          toast.success('Resposta enviada')
          handleOpenChange(false)
        },
        onError: (error) => {
          toast.error('Erro ao enviar resposta', {
            description: (error as Error).message || 'Tente novamente.',
          })
        },
      }
    )
  }

  const isSuggesting = suggestReply.isPending
  const isSending = replyToComment.isPending
  const isAiActive = isSuggesting || isRevealing
  const canSend = message.trim().length > 0 && !isSending && !isRevealing

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Responder comentário</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Comment preview */}
          <div className="rounded-[10px] bg-muted/50 dark:bg-muted/30 p-3 space-y-1">
            <p className="text-sm text-foreground leading-relaxed">
              &ldquo;{item.commentText || ''}&rdquo;
            </p>
            <p className="text-xs text-muted-foreground font-medium">
              {item.authorUsername ? `— @${item.authorUsername}` : '—'}
            </p>
            {(item.sentimentReason || item.sentimentScore != null) && (
              <p className="text-xs text-muted-foreground">
                {item.sentimentReason && `IA: ${item.sentimentReason}`}
                {item.sentimentReason && item.sentimentScore != null && (
                  <span className="mx-1.5 text-muted-foreground/50" aria-hidden="true">
                    &bull;
                  </span>
                )}
                {item.sentimentScore != null && `${Math.round(item.sentimentScore * 100)}%`}
              </p>
            )}
          </div>

          {/* Textarea with AI glow overlay */}
          <div className="space-y-1.5">
            <div
              className={cn(
                'relative rounded-md transition-all duration-500',
                isAiActive && 'ai-glow-border'
              )}
            >
              <div className="relative">
                {/* Word reveal overlay — sits on top of textarea during reveal */}
                {isRevealing && (
                  <div
                    className="absolute inset-0 rounded-md bg-card px-3 py-2 text-sm leading-relaxed pointer-events-none z-10 overflow-hidden"
                    aria-live="polite"
                    role="status"
                  >
                    {totalWords.map((word, i) => {
                      const isVisible = i < revealedWords.length
                      return (
                        <span
                          key={i}
                          className={cn(
                            'transition-all duration-300 inline',
                            isVisible
                              ? 'opacity-100 blur-0 text-foreground'
                              : 'opacity-0 blur-sm text-foreground'
                          )}
                        >
                          {word}
                        </span>
                      )
                    })}
                  </div>
                )}

                {/* Shimmer placeholder while waiting for API */}
                {isSuggesting && !isRevealing && (
                  <div className="absolute inset-0 rounded-md bg-card px-3 py-2 z-10 pointer-events-none flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-primary animate-pulse" />
                    <span className="text-sm text-muted-foreground ai-shimmer-text">
                      Gerando sugestão...
                    </span>
                  </div>
                )}

                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => {
                    if (!isRevealing) setMessage(e.target.value)
                  }}
                  placeholder="Sua resposta..."
                  maxLength={MAX_CHARS}
                  rows={3}
                  className={cn(
                    'shadow-recessed min-h-[80px] resize-none bg-card relative',
                    'focus-visible:ring-[3px] focus-visible:ring-[rgba(16,185,129,0.12)] focus-visible:border-[var(--primary)]',
                    isAiActive && 'border-transparent',
                    isSuggesting && !isRevealing && 'text-transparent'
                  )}
                  aria-label="Sua resposta"
                  disabled={isRevealing}
                />
              </div>
            </div>

            {/* Character counter */}
            <p className="text-xs text-muted-foreground text-right" aria-live="polite">
              {message.length}/{MAX_CHARS}
            </p>
          </div>

          {/* Unhide checkbox */}
          {item.action === 'hidden' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="unhide-on-reply"
                checked={unhide}
                onCheckedChange={(checked) => setUnhide(checked === true)}
              />
              <label
                htmlFor="unhide-on-reply"
                className="text-sm text-foreground cursor-pointer select-none"
              >
                Desocultar ao responder
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSuggest}
            disabled={isSuggesting || isSending || isRevealing}
            className={cn('gap-1.5 transition-all duration-200', isAiActive && 'text-primary')}
            aria-label={
              hasSuggested ? 'Gerar outra sugestão de resposta' : 'Sugerir resposta com IA'
            }
          >
            <Sparkles
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                isAiActive && 'animate-pulse scale-110'
              )}
              aria-hidden="true"
            />
            {hasSuggested ? 'Gerar outra' : 'Sugerir resposta'}
          </Button>

          <ChonkyButton type="button" onClick={handleSend} disabled={!canSend} loading={isSending}>
            Enviar
          </ChonkyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
